"use client";

import {
  Card,
  CollectionToolbar,
  Input,
  PageHeader,
  PageLayout,
  Stack,
  Text,
} from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, type ReactNode } from "react";
import { PolicyStateFeedback } from "../policy-state";
import { PolicyDirectoryPagination } from "./policy-directory-pagination";
import { PolicyCollection } from "./policy-collection";
import type { PoliciesDirectoryState } from "./policy-directory-records";

export type PoliciesDirectoryScreenProps = {
  readonly state: PoliciesDirectoryState;
  readonly title?: string;
  readonly description?: string;
  readonly uploadImages?: ReactNode;
  readonly collectionNavigation?: ReactNode;
  readonly collectionFilters?: ReactNode;
  readonly layout?: "page" | "workspace";
};

export function PoliciesDirectoryScreen({
  state,
  title = "Policies",
  description = "Browse policies ordered by creation date.",
  uploadImages,
  collectionNavigation,
  collectionFilters,
  layout = "page",
}: PoliciesDirectoryScreenProps) {
  const searchId = useId();
  const pending =
    state.status === "ready" &&
    (state.pending || state.refresh?.status === "refreshing");
  let feedback = state;

  if (state.status === "ready" && state.pending && !state.refresh)
    feedback = { ...state, refresh: { status: "refreshing" } };

  const Frame = layout === "workspace" ? "div" : Card;
  const searchInput = (
    <Input
      size={layout === "workspace" ? "sm" : "md"}
      id={searchId}
      type="search"
      aria-label="Search policies by name"
      placeholder={
        layout === "workspace" ? "Search policies by name" : "Search by name"
      }
      value={state.status === "ready" ? state.search : ""}
      disabled={state.status !== "ready"}
      onChange={(event) => {
        if (state.status === "ready") state.onSearchChange(event.target.value);
      }}
    />
  );
  return (
    <PageLayout variant={layout}>
      <PageHeader
        variant={layout}
        title={title}
        description={description}
        actions={
          <PolicyStateFeedback
            state={feedback}
            subject="policies"
            onRetry={state.status === "loading" ? undefined : state.onRetry}
          />
        }
      />
      {collectionNavigation}
      <Frame>
        <Stack width="auto" gap={24}>
          <search aria-label="Policy filters">
            {layout === "workspace" ? (
              <CollectionToolbar
                search={searchInput}
                filters={collectionFilters}
              />
            ) : (
              <label htmlFor={searchId} {...stylex.props(styles.search)}>
                <Text size="small">Search policies by name</Text>
                {searchInput}
              </label>
            )}
          </search>
          <div {...stylex.props(layout === "workspace" && styles.collection)}>
            <PolicyCollection state={state} layout={layout} />
          </div>
          <PolicyDirectoryPagination state={state} pending={pending} />
        </Stack>
      </Frame>
      {uploadImages}
    </PageLayout>
  );
}

const styles = stylex.create({
  search: {
    display: "grid",
    gap: spacing.space8,
    maxWidth: "360px",
    width: "100%",
  },
  collection: {
    height: "min(32rem, 55dvh)",
    overflowY: "auto",
  },
});
